import React from 'react';
import Axios, { CancelToken } from 'axios';

import useErrorHandler from './useErrorHandler';
const limit = 10;

const useList = <T extends any>(
  makeRequest: (newOffset: number, cancelToken: CancelToken) => Promise<T[]>,
  cancelMessage = 'Get data cancelled'
) => {
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [data, setData] = React.useState<T[]>([]);
  const cancel = React.useRef(Axios.CancelToken.source());
  const errorHandler = useErrorHandler();

  const handleRefresh = React.useCallback(async () => {
    try {
      cancel.current.cancel(cancelMessage);
      cancel.current = Axios.CancelToken.source();
      setRefreshing(true);
      const newData = await makeRequest(0, cancel.current.token);
      setData(newData);
      setOffset(limit);
      setRefreshing(false);
    } catch (e) {
      await errorHandler(e);
      setRefreshing(false);
    }
  }, [cancelMessage, errorHandler, makeRequest]);

  const handleEndReached = React.useCallback(async () => {
    try {
      if (loading) {
        return;
      }
      setLoading(true);
      const newData = await makeRequest(offset, cancel.current.token);
      setData([...data, ...newData]);
      setOffset(limit + offset);
      setLoading(false);
    } catch (e) {
      await errorHandler(e);
      setLoading(false);
    }
  }, [data, errorHandler, loading, makeRequest, offset]);

  React.useEffect(() => {
    handleRefresh().catch(console.log);
  }, [handleRefresh]);

  React.useEffect(() => () => cancel.current.cancel(cancelMessage), [
    cancelMessage,
  ]);

  return React.useMemo(
    () => ({
      data,
      handleEndReached,
      handleRefresh,
      refreshing,
      loading,
    }),
    [data, handleEndReached, handleRefresh, loading, refreshing]
  );
};

export default useList;
